/**
 * Free gift offers (sections/free-gift-offers.liquid).
 *
 * The section renders, for the current cart, each offer's state (qualifies? which gift lines are in
 * the cart?) plus the panel shown in the cart. This script:
 *  - copies that panel into every non-empty <cart-items> (cart drawer and cart page),
 *  - re-fetches the section whenever the cart re-renders (the theme replaces <cart-items> content,
 *    which removes our panel - that is the signal),
 *  - adds the gift (line property _free_gift = offer id) when the cart qualifies and there is one
 *    choice, and removes gift lines that no longer qualify, are charged, duplicated or orphaned,
 *  - adds the customer's pick from the panel's "Add free" buttons.
 */
(() => {
  if (window.freeGiftOffers) return;

  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const NO_DISCOUNT_MS = 15 * 60 * 1000;
  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

  let busy = false;
  let pendingRefresh = false;
  let lastActedState = null;
  let seq = 0;
  let opsWindowStart = 0;
  let opsInWindow = 0;

  const store = {
    get(k) {
      try { return sessionStorage.getItem(k); } catch (e) { return null; }
    },
    set(k, v) {
      try {
        if (v === null) sessionStorage.removeItem(k);
        else sessionStorage.setItem(k, v);
      } catch (e) { /* storage blocked: offer still works, just without memory */ }
    }
  };

  const isDeclined = (key) => store.get(`fgo-declined-${key}`) === '1';
  const setDeclined = (key, on) => store.set(`fgo-declined-${key}`, on ? '1' : null);
  const noDiscount = (key) => {
    const t = Number(store.get(`fgo-nodiscount-${key}`));
    return t && Date.now() - t < NO_DISCOUNT_MS;
  };

  const source = () => document.querySelector('[data-fgo-source]');

  function readState() {
    const el = source() && source().querySelector('[data-fgo-state]');
    if (!el) return null;
    try {
      return { text: el.textContent, data: JSON.parse(el.textContent) };
    } catch (e) {
      console.warn('Free gift offers: bad state', e); // eslint-disable-line
      return null;
    }
  }

  /** Copies the panel into each non-empty cart, replacing any older copy. */
  function insertPanels() {
    const src = source();
    if (!src) return;
    const html = src.querySelector('[data-fgo-panel-html]').innerHTML.trim();
    const state = readState();

    document.querySelectorAll('cart-items').forEach((cartItems) => {
      const old = cartItems.querySelector(':scope > .fgo-panel');
      if (old) old.remove();
      if (cartItems.dataset.empty === 'true' || !cartItems.querySelector('.cart-item')) return;

      const panel = document.createElement('div');
      panel.className = 'fgo-panel';
      panel.innerHTML = html;
      panel.hidden = !html;

      // Hide the picker while an auto-add is about to happen (no flash), and when the offer's
      // discount was found missing (a picked gift would only be taken out again).
      if (state) {
        state.data.offers.forEach((offer) => {
          const picker = panel.querySelector(`[data-fgo-offer="${offer.key}"][data-fgo-picker]`);
          if (!picker) return;
          const autoPending = offer.auto && offer.qualifies && !offer.lines.length && !isDeclined(offer.key);
          if (autoPending || noDiscount(offer.key)) picker.hidden = true;
        });
        if (!panel.querySelector('.fgo-offer:not([hidden])')) panel.hidden = true;
      }

      const table = cartItems.querySelector(':scope > table');
      if (table) table.after(panel);
      else cartItems.prepend(panel);
    });
  }

  /** Re-renders the theme's cart (drawer or cart page) after we changed the cart. */
  function refreshThemeCart() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.refresh === 'function') {
      drawer.refresh();
      return;
    }
    const cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.refresh === 'function' && cartItems.dataset.empty === 'false') {
      cartItems.refresh();
    } else {
      window.location.reload();
    }
  }

  async function post(url, body) {
    const res = await fetch(`${root}${url}`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify(body) });
    if (!res.ok) throw new Error(`${url} ${res.status}`);
    return res.json();
  }

  /** Works out the cart changes the state calls for. */
  function plan(state) {
    const changes = []; // { id: line key, quantity }
    let add = null; // { id: variant id, key }

    state.orphans.forEach((lineKey) => changes.push({ id: lineKey, quantity: 0 }));

    state.offers.forEach((offer) => {
      if (!offer.on || !offer.qualifies) {
        offer.lines.forEach((l) => changes.push({ id: l.key, quantity: 0 }));
        if (!offer.qualifies) setDeclined(offer.key, false);
        return;
      }

      let kept = null;
      offer.lines.forEach((l) => {
        if (!l.eligible || kept) {
          changes.push({ id: l.key, quantity: 0 });
        } else if (l.qty > 1) {
          kept = l;
          changes.push({ id: l.key, quantity: 1 });
        } else if (l.paid) {
          // The discount didn't make it free: never sell a gift by accident.
          store.set(`fgo-nodiscount-${offer.key}`, String(Date.now()));
          console.warn(`Free gift offers: offer ${offer.key} gift is not free - is its discount active?`); // eslint-disable-line
          changes.push({ id: l.key, quantity: 0 });
        } else {
          kept = l;
        }
      });

      if (!kept && !offer.lines.length && offer.auto && !add
        && !isDeclined(offer.key) && !noDiscount(offer.key)) {
        add = { id: offer.auto, key: offer.key };
      }
    });

    return { changes, add };
  }

  function rateLimited() {
    const now = Date.now();
    if (now - opsWindowStart > 30000) {
      opsWindowStart = now;
      opsInWindow = 0;
    }
    opsInWindow += 1;
    return opsInWindow > 8;
  }

  async function reconcile() {
    const state = readState();
    if (!state || state.text === lastActedState) return;

    const { changes, add } = plan(state.data);
    if (!changes.length && !add) return;
    if (rateLimited()) {
      console.warn('Free gift offers: too many cart changes, pausing'); // eslint-disable-line
      return;
    }

    lastActedState = state.text;
    busy = true;
    try {
      for (const change of changes) {
        await post('cart/change.js', change); // eslint-disable-line no-await-in-loop
      }
      if (add) {
        await post('cart/add.js', { items: [{ id: add.id, quantity: 1, properties: { _free_gift: add.key } }] });
      }
    } catch (e) {
      console.warn('Free gift offers:', e); // eslint-disable-line
    } finally {
      busy = false;
      refreshThemeCart();
    }
  }

  /** Fetches this section again so its state matches the cart, then shows it and acts on it. */
  async function refresh() {
    if (busy) {
      pendingRefresh = true;
      return;
    }
    const src = source();
    if (!src) return;
    const id = src.dataset.sectionId;
    const mine = ++seq;

    try {
      const res = await fetch(`${window.location.pathname}?sections=${encodeURIComponent(id)}`);
      if (!res.ok) throw new Error(res.status);
      const sections = await res.json();
      if (mine !== seq || !sections[id]) return;

      const tmpl = document.createElement('template');
      tmpl.innerHTML = sections[id];
      const fresh = tmpl.content.querySelector('[data-fgo-source]');
      if (fresh) src.innerHTML = fresh.innerHTML;

      // Only a state we already acted on and that did not change (e.g. the add failed) is skipped.
      const now = readState();
      if (now && now.text !== lastActedState) lastActedState = null;
    } catch (e) {
      console.warn('Free gift offers: refresh failed', e); // eslint-disable-line
      return;
    }

    insertPanels();
    await reconcile();

    if (pendingRefresh) {
      pendingRefresh = false;
      refresh();
    }
  }

  /** A cart that lost our panel has just been re-rendered by the theme. */
  function cartNeedsPanel() {
    return Array.from(document.querySelectorAll('cart-items')).some((el) => el.dataset.empty !== 'true'
      && el.querySelector('.cart-item') && !el.querySelector(':scope > .fgo-panel'));
  }

  let timer;
  const observer = new MutationObserver(() => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      if (cartNeedsPanel()) refresh();
    }, 120);
  });

  async function addPicked(button) {
    const key = button.dataset.fgoAdd;
    const choice = button.closest('.fgo-choice');
    const select = choice && choice.querySelector('[data-fgo-select]');
    if (!select || !select.value) return;

    button.disabled = true;
    button.classList.add('is-loading');
    setDeclined(key, false);
    busy = true;
    try {
      await post('cart/add.js', { items: [{ id: Number(select.value), quantity: 1, properties: { _free_gift: key } }] });
    } catch (e) {
      console.warn('Free gift offers: add failed', e); // eslint-disable-line
    } finally {
      busy = false;
      refreshThemeCart();
    }
  }

  document.addEventListener('click', (evt) => {
    const addBtn = evt.target.closest('[data-fgo-add]');
    if (addBtn) {
      evt.preventDefault();
      addPicked(addBtn);
      return;
    }
    // Customer removed a gift themselves: don't push it back in this visit (the picker stays).
    const remove = evt.target.closest('.js-remove-item');
    const giftRow = remove && remove.closest('.cart-item--gift');
    if (giftRow && giftRow.dataset.fgoKey) setDeclined(giftRow.dataset.fgoKey, true);
  }, true);

  function start() {
    insertPanels();
    reconcile();
    observer.observe(document.body, { childList: true, subtree: true });
  }

  window.freeGiftOffers = { refresh, reconcile };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
